import { Box, Chip, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useEffect, useState } from "react";
import { WorkItemInputField } from "./WorkItemInputStyles";
import DeleteableChips from '../../shared/select/DeletableChips';
import ChipWithTooltip from "components/shared/select/ChipWithTooltip";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MenuTopProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  anchorOrigin: {
    vertical: "top",
    horizontal: "left"
  },
  transformOrigin: {
    vertical: "bottom",
    horizontal: "left"
  }
}

interface IWorkItemSelectFieldProps {
  label: string,
  labelProps?: SxProps<Theme> | undefined,
  idSuffix: string,
  options?: any[],
  onChange: (e: number[]) => void,
  multiple: boolean,
  optionPosition?: 'top' | 'bottom'
}

const WorkItemSelectField = (props: IWorkItemSelectFieldProps) => {
  const theme = useTheme();
  const [workItemStatus, setWorkItemStatus] = useState<any[]>([]);

  const getStyles = (status: string, wiStatus: readonly string[], theme: Theme) => {
    return {
      fontWeight:
        wiStatus.indexOf(status) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }

  const handleChangeMultiple = (event: SelectChangeEvent<typeof workItemStatus>) => {
    const {
      target: { value },
    } = event;

    setWorkItemStatus(value as any[]);
    const ids = (value as any[]).map(status => +status.id);
    props.onChange(ids);
  };

  const handleChangeSingle = (event: SelectChangeEvent<typeof workItemStatus>) => {
    const {
      target: { value },
    } = event;
    const ids = [(value as any).id];
    props.onChange(ids);
  };


  const handleClear = (id: string) => {
    const filterData = workItemStatus.filter((val) => val.id !== id);
    setWorkItemStatus(filterData);
    const ids = filterData.map(status => +status.id);
    props.onChange(ids);

  }

  const displayWithMoreValues = (chipItems: any[], rest: any[]) => {
    return (
      <>
        {chipItems.map((value) => (<DeleteableChips key={value.id} id={value.id} name={value.name} onDelete={handleClear} />))}
        {rest.length > 0 && <ChipWithTooltip count={rest.length} id={rest.map(x => x.id).join(',')} message={rest.map(x => x.name).join(', ')} />}
      </>
    )
  }

  const displayValues = (selected: any[]) => {
    const maxLength = devicePixelRatio > 1.3 ? 30 : devicePixelRatio > 1.2 ? 35 : devicePixelRatio > 1 ? 36 : 42;
    let charLength = 0;
    let chipItems: any[] = [];
    for (let i = 0; i < (Math.min(3, selected.length)); i++) {
      charLength += selected[i].name.length + 4;
      if (charLength < maxLength) {
        chipItems = [...chipItems, selected[i]];
      }
      if (charLength > maxLength) {
        break;
      }
    }
    const rest = selected.filter(x => !chipItems.includes(x));
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.length > chipItems.length ? displayWithMoreValues(chipItems, rest) :

          selected.map((value) => (
            props.multiple ?
              <DeleteableChips key={value.id} id={value.id} name={value.name} onDelete={handleClear} />
              : <Chip key={value.id} label={value.name} />
          ))
        }
      </Box>
    )
  }

  useEffect(() => {
    if (props.options && props.options.length > 0 && workItemStatus.length > 0) {
      const newValues = workItemStatus.filter(option => props.options?.some(status => status.id === option.id));
      setWorkItemStatus(newValues);
      const ids = newValues.map(status => +status.id);
      props.onChange(ids);
    } else {
      setWorkItemStatus([]);
      props.onChange([]);
    }
  }, [props.options]);

  return (
    <FormControl sx={{ paddingRight: 1 }} variant="standard" fullWidth>
      <InputLabel 
        shrink 
        sx={props.labelProps}
        id={"search-work-item-status-label-"+props.idSuffix}>{props.label}:</InputLabel>
      <Select
        labelId="work-item-status-label"
        id={"search-work-item-status-"+props.idSuffix}
        multiple={props.multiple}
        disabled={props.options === undefined || props.options.length === 0}
        value={props.multiple ? workItemStatus : undefined}
        onChange={props.multiple ? handleChangeMultiple : handleChangeSingle}
        input={<WorkItemInputField />}
        renderValue={(selected) => {
          return props.multiple ? displayValues(selected as any[]) :
            (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as any).name}
              </Box>
            )
        }}
        MenuProps={props.optionPosition === 'top' ? MenuTopProps : MenuProps}
      >
        {props.options && props.options.map((option) => (
          <MenuItem
            key={option.id}
            value={option}
            style={getStyles(option.name, workItemStatus, theme)}
          >
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default WorkItemSelectField;